import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Button,
    Card,
    Descriptions,
    Drawer,
    Input,
    message,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {DiffOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, SyncOutlined} from '@ant-design/icons';
import useApiPermission from '../hooks/useApiPermission';
import {
    createDbListener,
    getEventById,
    getListenersByEvent,
    getUnifiedListeners,
    updateListener,
    updateListenerUse
} from '../api/systemEventApi';
import {pickData, pickList} from '../api/http';
import CompareListenerModal from './CompareListenerModal';

const GlobalListenersTab = ({ setHeaderActions = () => {} }) => {
  const { canCall, loading: permLoading } = useApiPermission();
  const canReadAll = canCall(getUnifiedListeners);
  const canUpdateListener = canCall(updateListenerUse);
  const canCreateListener = canCall(createDbListener);
  const canReadEventOne = canCall(getEventById);

  const [loading, setLoading] = useState(false);
  const [listeners, setListeners] = useState([]);

  const [eventNameQuery, setEventNameQuery] = useState('');
  const [classQuery, setClassQuery] = useState('');
  const [methodQuery, setMethodQuery] = useState('');
  const [descQuery, setDescQuery] = useState('');
  const [useFilter, setUseFilter] = useState('all'); // all | Y | N
  const [regFilter, setRegFilter] = useState('all'); // all | 등록 | 미등록 | 삭제 | 변경
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [batchRegistering, setBatchRegistering] = useState(false);
  const [batchSyncing, setBatchSyncing] = useState(false);
  const [listenerDesc, setListenerDesc] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [eventLoading, setEventLoading] = useState(false);

  const [compareOpen, setCompareOpen] = useState(false);
  const [compareRow, setCompareRow] = useState(null);

  const fetchAll = useCallback(async () => {
    if (permLoading) return;
    setLoading(true);
    try {
      if (!canReadAll) {
        setListeners([]);
        message.warning('전역 리스너 조회 권한이 없습니다.');
        return;
      }
      const rs = await getUnifiedListeners();
      const list = pickList(rs);
      const rows = (Array.isArray(list) ? list : []).map((d, idx) => ({
        key: d.listenerId || `${d.beanClass || 'cls'}::${d.methodName || 'm'}::${idx}`,
        listenerId: d.listenerId,
        eventId: d.eventId,
        eventKey: d.eventKey,
        eventName: d.eventName || d.eventKey,
        beanName: d.beanName,
        beanClass: d.beanClass,
        methodName: d.methodName,
        kind: d.kind,
        useYn: (d.useYn || 'Y').toUpperCase(),
        registerType: d.registerType || '-',
        description: d.dsctn || '',
      }));
      rows.sort((a,b)=> (a.eventName||'').localeCompare(b.eventName||'')
        || (a.beanClass||'').localeCompare(b.beanClass||'')
        || (a.methodName||'').localeCompare(b.methodName||''));
      setListeners(rows);
    } catch (e) {
      message.error('전역 리스너 목록 조회 실패');
      setListeners([]);
    } finally {
      setLoading(false);
    }
  }, [permLoading, canReadAll]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // 선택 등록 핸들러
  const handleBatchRegister = useCallback(async () => {
    if (permLoading || !canCreateListener) { message.warning('리스너 등록 권한이 없습니다.'); return; }
    const targets = listeners.filter(r => selectedKeys.includes(r.key) && !r.listenerId);
    if (!targets.length) { message.warning('등록할 런타임 리스너를 선택하세요.'); return; }
    setBatchRegistering(true);
    let success = 0, fail = 0, skipped = 0;
    const ds = (listenerDesc||'').trim();
    for (const t of targets) {
      try {
        if (!t.eventId) { skipped++; continue; }
        await createDbListener({ eventKey: t.eventKey, kind: t.kind, beanName: t.beanName, beanClass: t.beanClass, methodName: t.methodName, useYn: 'Y', dsctn: ds || undefined });
        success++;
      } catch (_) { fail++; }
    }
    if (success) message.success(`등록 완료: ${success}건`);
    if (fail) message.error(`등록 실패: ${fail}건`);
    if (skipped) message.warning(`이벤트 미등록으로 건너뜀: ${skipped}건 (이벤트를 먼저 등록하세요)`);
    setSelectedKeys([]);
    await fetchAll();
    setBatchRegistering(false);
  }, [permLoading, canCreateListener, listeners, selectedKeys, fetchAll, listenerDesc]);

  // 설명 불일치 항목 선택갱신(일괄)
  const handleBatchSyncDescriptions = useCallback(async () => {
    if (permLoading || !canUpdateListener) { message.warning('리스너 갱신 권한이 없습니다.'); return; }
    const selected = listeners.filter(r => selectedKeys.includes(r.key));
    if (!selected.length) { message.warning('갱신할 리스너를 선택하세요.'); return; }
    // DB 리스너만 대상
    const targets = selected.filter(r => !!r.listenerId && !!r.eventId);
    if (!targets.length) { message.warning('DB 리스너를 선택하세요. (등록된 리스너만 갱신 가능)'); return; }
    setBatchSyncing(true);
    try {
      // eventKey 별로 런타임 리스너 조회 후 매칭
      const group = targets.reduce((m, r) => { (m[r.eventKey] ||= []).push(r); return m; }, {});
      let updated = 0, skipped = 0, failed = 0;
      for (const [eventKey, rows] of Object.entries(group)) {
        try {
          const res = await getListenersByEvent(eventKey);
          const rtList = pickList(res) || [];
          const rtMap = new Map(rtList.map(li => [`${li.beanClass}::${li.methodName}`, (li.description || '').trim()]));
          for (const r of rows) {
            const key = `${r.beanClass}::${r.methodName}`;
            const rtDesc = (rtMap.get(key) || '').trim();
            const dbDesc = (r.description || '').trim();
            if (!rtDesc) { skipped++; continue; } // 런타임에 없거나 설명 제공 안됨
            if (rtDesc === dbDesc) { skipped++; continue; }
            try {
              await updateListener(r.listenerId, { dsctn: rtDesc });
              updated++;
            } catch (_) { failed++; }
          }
        } catch (_) {
          // 해당 이벤트의 런타임 조회 실패: 전부 스킵 처리
          skipped += rows.length;
        }
      }
      if (updated) message.success(`설명 갱신: ${updated}건`);
      if (skipped) message.warning(`건너뜀: ${skipped}건`);
      if (failed) message.error(`실패: ${failed}건`);
      await fetchAll();
      setSelectedKeys([]);
    } finally {
      setBatchSyncing(false);
    }
  }, [permLoading, canUpdateListener, listeners, selectedKeys, fetchAll]);

  // 드로어 오픈: 이벤트 상세 로드(가능 시)
  const openDrawer = useCallback(async (row) => {
    setSelected(row);
    setEventDetail(null);
    setDrawerOpen(true);
    if (!row?.eventId || !canReadEventOne) return;
    try {
      setEventLoading(true);
      const res = await getEventById(row.eventId);
      const dto = pickData(res);
      setEventDetail(dto || null);
    } catch (_) {
      setEventDetail(null);
    } finally {
      setEventLoading(false);
    }
  }, [canReadEventOne]);

  const closeDrawer = () => { setDrawerOpen(false); setSelected(null); setEventDetail(null); };
  const closeCompare = () => { setCompareOpen(false); setCompareRow(null); };
  // 비교 모달 오픈 핸들러 추가
  const openCompare = (row) => { setCompareRow(row); setCompareOpen(true); };
  const onComparedApplied = async () => { await fetchAll(); };

  // 헤더 액션(UI) 구성 및 부모로 전달
  useEffect(() => {
    const header = (
      <>
        <Space wrap>
          <Input allowClear placeholder="이벤트명/키" value={eventNameQuery} onChange={e=>setEventNameQuery(e.target.value)} style={{ width: 200 }} />
          <Input allowClear placeholder="클래스/빈" value={classQuery} onChange={e=>setClassQuery(e.target.value)} style={{ width: 220 }} />
          <Input allowClear placeholder="메서드" value={methodQuery} onChange={e=>setMethodQuery(e.target.value)} style={{ width: 160 }} />
          <Input allowClear placeholder="설명 검색" value={descQuery} onChange={e=>setDescQuery(e.target.value)} style={{ width: 200 }} />
          <Select value={useFilter} onChange={setUseFilter} style={{ width: 140 }} options={[
            { value: 'all', label: '사용여부: 전체' },
            { value: 'Y', label: '사용' },
            { value: 'N', label: '중지' },
          ]} />
          <Select value={regFilter} onChange={setRegFilter} style={{ width: 180 }} options={[
            { value: 'all', label: '등록구분: 전체' },
            { value: '등록', label: '등록' },
            { value: '변경', label: '변경' },
            { value: '미등록', label: '미등록' },
            { value: '삭제', label: '삭제' },
          ]} />
          <Input allowClear placeholder="설명(등록 시)" value={listenerDesc} onChange={e=>setListenerDesc(e.target.value)} style={{ width: 240 }} />
          <Tooltip title="목록 새로고침">
            <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading} disabled={permLoading || !canReadAll}>새로고침</Button>
          </Tooltip>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleBatchRegister} loading={batchRegistering} disabled={permLoading || !canCreateListener || !selectedKeys.length}>
            선택등록
          </Button>
          <Button icon={<SyncOutlined />} onClick={handleBatchSyncDescriptions} loading={batchSyncing} disabled={permLoading || !canUpdateListener || !selectedKeys.length}>
            선택갱신(설명)
          </Button>
        </Space>
      </>
    );
    setHeaderActions(header);
    return () => setHeaderActions(null);
  }, [eventNameQuery, classQuery, methodQuery, descQuery, useFilter, regFilter, loading, permLoading, canReadAll, fetchAll, setHeaderActions, handleBatchRegister, canCreateListener, selectedKeys, batchRegistering, listenerDesc, handleBatchSyncDescriptions, batchSyncing, canUpdateListener]);

  const toggleUse = async (row) => {
    try {
      if (permLoading || !canUpdateListener) { message.warning('리스너 수정 권한이 없습니다.'); return; }
      if (!row.listenerId) { message.warning('DB 리스너가 아닙니다.'); return; }
      const next = (row.useYn || 'Y') === 'Y' ? 'N' : 'Y';
      await updateListenerUse(row.listenerId, next);
      message.success(`사용여부가 '${next}'로 변경되었습니다.`);
      fetchAll();
    } catch (e) {
      message.error('변경 실패');
    }
  };

  const filtered = useMemo(() => {
    let list = listeners;
    const eQ = (eventNameQuery||'').trim().toLowerCase();
    const cQ = (classQuery||'').trim().toLowerCase();
    const mQ = (methodQuery||'').trim().toLowerCase();
    const dQ = (descQuery||'').trim().toLowerCase();

    if (eQ) list = list.filter(r => (r.eventName||'').toLowerCase().includes(eQ) || (r.eventKey||'').toLowerCase().includes(eQ));
    if (cQ) list = list.filter(r => (r.beanClass||'').toLowerCase().includes(cQ) || (r.beanName||'').toLowerCase().includes(cQ));
    if (mQ) list = list.filter(r => (r.methodName||'').toLowerCase().includes(mQ));
    if (dQ) list = list.filter(r => (r.description||'').toLowerCase().includes(dQ));

    if (useFilter !== 'all') list = list.filter(r => (r.listenerId ? (r.useYn||'Y') : 'Y') === useFilter);
    if (regFilter !== 'all') list = list.filter(r => (r.registerType||'-') === regFilter);
    return list;
  }, [listeners, eventNameQuery, classQuery, methodQuery, descQuery, useFilter, regFilter]);

  const columns = [
    { title: '등록구분', dataIndex: 'registerType', key: 'registerType', width: 100, align:'center', 
      sorter: (a, b) => {
        const rank = (t) => { if (t === '삭제') return 0; if (t === '변경') return 1; if (t === '미등록') return 2; if (t === '등록') return 3; return 99; };
        return rank(a.registerType || '') - rank(b.registerType || '');
      },
      render: (t) => {
        const color = t==='삭제' ? 'volcano' : (t==='미등록' ? 'red' : (t==='등록' ? 'green' : (t==='변경' ? 'orange' : undefined)));
        return <Tag color={color}>{t || '-'}</Tag>;
      }
    },
    { title: '사용여부', dataIndex: 'useYn', key: 'useYn', width: 110, align:'center', 
      sorter: (a, b) => ((a.useYn || 'Y')).localeCompare((b.useYn || 'Y')),
      render: (v, r) => (
        <Switch checked={(r.listenerId ? (v||'Y')==='Y' : true)} onChange={() => toggleUse(r)} disabled={permLoading || !canUpdateListener || !r.listenerId} />
      ) 
    },
    { title: '설명', dataIndex: 'description', key: 'description', ellipsis: true, sorter: (a, b) => (a.description || '').localeCompare(b.description || '') },
    // 이벤트명 대신 빈 항목으로 컬럼 교체
    { title: '빈', dataIndex: 'beanName', key: 'beanName', width: 200, sorter: (a, b) => (a.beanName || '').localeCompare(b.beanName || ''), render: (v) => v || '-' },
    { title: '클래스', dataIndex: 'beanClass', key: 'beanClass', ellipsis: true, sorter: (a, b) => (a.beanClass || '').localeCompare(b.beanClass || '') },
    { title: '작업', key: 'act', width: 180, render: (_, r) => (
      <Space size={4}>
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDrawer(r)}>상세</Button>
        <Button size="small" icon={<DiffOutlined />} onClick={() => openCompare(r)}>비교</Button>
      </Space>
    ) },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <Card size="small" styles={{ body: { padding: 0, overflowX: 'auto' } }}>
        <Table
          size="small"
          rowKey={r=>r.key}
          columns={columns}
          dataSource={filtered}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys),
            getCheckboxProps: (r) => ({ disabled: !r.eventId })
          }}
          onRow={(record) => ({ onDoubleClick: () => openDrawer(record) })}
        />
      </Card>

      {/* 리스너 상세 드로어 */}
      <Drawer
        title={selected ? (selected.eventName || selected.eventKey || '리스너 상세') : '리스너 상세'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={680}
      >
        {selected && (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>리스너 정보</Typography.Title>
              <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                <Descriptions.Item label="등록구분"><Tag>{selected.registerType || '-'}</Tag></Descriptions.Item>
                <Descriptions.Item label="리스너ID">{selected.listenerId || '-'}</Descriptions.Item>
                <Descriptions.Item label="종류">{selected.kind || '-'}</Descriptions.Item>
                <Descriptions.Item label="빈">{selected.beanName || '-'}</Descriptions.Item>
                <Descriptions.Item label="클래스">{selected.beanClass || '-'}</Descriptions.Item>
                <Descriptions.Item label="메서드">{selected.methodName || '-'}</Descriptions.Item>
                <Descriptions.Item label="설명">{selected.description || '-'}</Descriptions.Item>
                <Descriptions.Item label="사용여부">{selected.listenerId ? ((selected.useYn||'Y').toUpperCase()==='Y' ? 'Y' : 'N') : '-'}</Descriptions.Item>
              </Descriptions>
            </div>

            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>이벤트 정보</Typography.Title>
              <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                <Descriptions.Item label="이벤트명">{selected.eventName || '-'}</Descriptions.Item>
                <Descriptions.Item label="키(클래스)">{selected.eventKey || '-'}</Descriptions.Item>
                <Descriptions.Item label="이벤트ID">{eventDetail?.eventId || selected.eventId || '-'}</Descriptions.Item>
                <Descriptions.Item label="설명">{eventLoading ? '로딩...' : (eventDetail?.eventExpln || '-')}</Descriptions.Item>
                <Descriptions.Item label="사용여부">{eventLoading ? '로딩...' : (eventDetail?.useYn || (selected.eventId ? 'Y' : '-'))}</Descriptions.Item>
                <Descriptions.Item label="디스패치">{eventLoading ? '로딩...' : (eventDetail?.dispatchType || (selected.eventId ? 'SYNC' : '-'))}</Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        )}
      </Drawer>
      <CompareListenerModal open={compareOpen} onClose={closeCompare} row={compareRow} onApplied={onComparedApplied} />
    </div>
  );
};

export default GlobalListenersTab;
